import { useContext } from 'react';
import { Icon, IconButton, Typography, Box, Stack } from '@ace-clublabs/shared-component-library';
import { MembershipAppStateContext } from '../MembershipAppState/MembershipAppState';
import { ProductType } from '../../application/models/NewJoinSessionData';
import { getProductPrice, eligibleForERS } from '../../utils/helpers';
import { getClubContent, ClubContentName, FEE_AMOUNT_PLACEHOLDER } from '../../utils/getClubContent';
import { ClubCode } from '../../utils/constants';
import { AddOn } from './AddOn';
import { AddedInfoCard } from './AddedInfoCard';

export interface ERSAddOnPros {
  setTooltipInfo: Function;
  setTooltipContent: Function;
  setDeleteInfo: Function;
  selectedERS: boolean;
}

export const ERSAddOn: (props: ERSAddOnPros) => JSX.Element = ({
  setTooltipInfo,
  setTooltipContent,
  setDeleteInfo,
  selectedERS,
}) => {
  const { membershipInfo, sessionData, updateProducts } = useContext(MembershipAppStateContext);
  const ersFee = membershipInfo?.membershipCost?.fees?.sameDayServiceFee;
  const ERSNotEligibleContent = (
    getClubContent(ClubContentName.ERSNotEligibleContent, sessionData.clubCode!) as string
  ).replaceAll(FEE_AMOUNT_PLACEHOLDER, `${ersFee}`);
  return (
    <>
      <Box className="benefit-add-ons">
        <Stack direction="row" alignItems="center" gap="0.5rem">
          <Icon
            iconName="truck-duotone"
            className="benefit-add-ons-icon"
            sx={{ color: '#0C7A93', fontSize: '1.5rem' }}
          />
          <Typography className="benefit-add-ons-title" variant="b1Medium">
            Immediate Roadside Assistance
          </Typography>
        </Stack>

        {eligibleForERS(membershipInfo, sessionData) ? (
          <IconButton
            aria-label="Immediate Roadside Assistance info"
            className="add-immediate-roadside-info-icon"
            onClick={() => {
              setTooltipInfo({
                tooltipTitle: 'Immediate Roadside Assistance',
              });
              setTooltipContent(
                `If you have a vehicle disablement before you join, that disablement will not be covered after you join as one of your four allowable service calls per membership year.  If you would like such service, you may request service for an additional $${ersFee} non-refundable service fee.`
              );
            }}
          >
            <Icon iconName="circle-info-regular" sx={{ color: '#707070' }} />
          </IconButton>
        ) : (
          <></>
        )}
      </Box>
      {eligibleForERS(membershipInfo, sessionData) ? (
        <>
          {sessionData.clubCode !== ClubCode.calif ? (
            <AddedInfoCard
              className="ers-info"
              content={`A $${ersFee} non-refundable service fee applies each time you use Roadside Assistance for the first three days of your new membership.  Plus®, Premier®, and RV/motorcycle benefits take effect after 7 days. Classic benefits start the first day subject to the service fee.`}
            />
          ) : selectedERS ? (
            <AddedInfoCard
              title="Added"
              content={`An additional $${ersFee} non-refundable service fee will be added to your purchase today for Roadside Assistance for a vehicle disablement that occurred before you joined. That disablement is not covered after you join as one of your four allowable service calls per membership year.`}
              className="ers-added-card"
              originalPrice={ersFee}
              priceSuffix="once"
              deleteButtonOnClick={() => {
                setDeleteInfo({
                  title: 'Are you sure you want to remove same day roadside service?',
                  warningContent: `If you have a vehicle disablement before you join, that disablement will not be covered after you join as one of your four allowable service calls per membership year. If you would like such service, you may request service for an additional $${ersFee} non-refundable service fee.`,
                  confirmRemove: () => {
                    updateProducts('remove', ProductType.ERS);
                  },
                });
              }}
            />
          ) : (
            <AddOn
              onAdd={() => updateProducts('add', ProductType.ERS)}
              text="Get roadside help now"
              price={getProductPrice(membershipInfo, ProductType.ERS, sessionData)}
              isSelected={false}
              icon="plus-regular"
              type={ProductType.ERS}
              priceSuffix="once"
            />
          )}
        </>
      ) : (
        <>
          <AddedInfoCard className="ers-not-eligible-info" content={ERSNotEligibleContent} />
        </>
      )}
    </>
  );
};
